import { useEffect, useRef, useState } from "react";
import { LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/i18n/useLanguage";

export interface ServiceSectionData {
  id: string;
  index: number;
  eyebrowLabel: string;
  title: string;
  icon: LucideIcon;
  duration: string;
  complexity: string;
  intro: string;
  serviceDescription: string;
  explorationPoints: string[];
  process: string;
  result: string;
}

interface ServiceSectionProps {
  service: ServiceSectionData;
}

const ServiceSection = ({ service }: ServiceSectionProps) => {
  const { t } = useTranslation();
  const { localize } = useLanguage();
  const Icon = service.icon;
  const indexLabel = String(service.index).padStart(2, "0");
  const ref = useRef<HTMLElement>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setRevealed(true);
            observer.disconnect();
            break;
          }
        }
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.05 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      id={service.id}
      className={cn(
        "scroll-mt-28 py-16 lg:py-24 border-t border-border/40 first:border-t-0 first:pt-0",
        "transition-all duration-700 ease-out",
        revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      )}
    >
      <div className="max-w-3xl">
        {/* Header */}
        <header className="mb-10">
          <div className="flex items-center gap-3 mb-5">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-brand shadow-glow-primary">
              <Icon className="w-5 h-5 text-primary-foreground" />
            </div>
            <p className="text-sm font-semibold tracking-[0.2em] text-primary uppercase">
              {indexLabel} — {service.eyebrowLabel}
            </p>
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground leading-[1.1] tracking-tight">
            {service.title}
          </h2>
          <div className="h-1 w-20 rounded-full bg-gradient-brand mt-5" />
        </header>

        {/* Intro pull-quote */}
        <p className="text-lg lg:text-xl text-foreground/95 leading-relaxed border-l-2 border-primary/60 pl-5 mb-8">
          {service.intro}
        </p>

        <div className="space-y-7">
          <p className="text-base lg:text-lg text-muted-foreground leading-relaxed">
            {service.serviceDescription}
          </p>

          <div>
            <p className="text-base font-semibold mb-4 text-foreground">
              {t("servicesPage.ui.explorationHeading")}
            </p>
            <ul className="space-y-3">
              {service.explorationPoints.map((point, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <span
                    aria-hidden
                    className="mt-2.5 inline-block w-1.5 h-1.5 rounded-full bg-gradient-brand shrink-0"
                  />
                  <span className="text-base lg:text-lg text-muted-foreground leading-relaxed">
                    {point}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <p className="text-base lg:text-lg text-muted-foreground leading-relaxed">
            {service.process}
          </p>

          {/* Result callout */}
          <div className="rounded-xl border border-primary/30 bg-primary/5 p-5 lg:p-6">
            <p className="text-[11px] font-semibold tracking-[0.2em] text-primary uppercase mb-2">
              {t("servicesPage.ui.outcomeLabel")}
            </p>
            <p className="text-base lg:text-lg font-medium text-foreground leading-relaxed">
              {service.result}
            </p>
          </div>

          <div className="pt-2">
            <Button asChild size="lg">
              <Link to={localize("/contact")}>{t("servicesPage.ui.cta")}</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceSection;
